/**
 * SubdomainSync Component
 * 
 * Synchronizes subdomain between three sources:
 * 1. Backend session_data (from page configurations)
 * 2. Redux store (frontend state)
 * 3. URL path (browser location)
 * 
 * Flow:
 * - When session_data has subdomain, update Redux store
 * - When Redux store has subdomain but URL doesn't, update URL
 * - When Redux store doesn't have subdomain but URL does, remove from URL
 */
import React, { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { setSubdomain, clearSubdomain } from '../../state/features/subdomain';
import { getRelativePath, getSubdomainPath, getCurrentSubdomain } from '../../helpers/pathUtils';

const SubdomainSync: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const session = useSelector((state: any) => state.session);
  const sessionSubdomain = session?.subdomain;
  const currentSubdomain = useSelector((state: any) => state.subdomain);
  const urlSubdomain = getCurrentSubdomain(location.pathname);
  const isInitialMount = useRef(true);
  const hasUpdatedUrl = useRef(false);

  // Sync subdomain from session_data with Redux store
  useEffect(() => {
    // Check if session has been loaded (has meaningful data beyond initial state)
    // The initial state has empty strings, so if session has real data, it's been loaded
    // This prevents random updates when the store is not yet updated
    const sessionHasBeenLoaded = session && (
      session.area_name || 
      (session.area_id && session.area_id !== '') ||
      (session.outlet_id && session.outlet_id !== '')
    );

    // Only update subdomain if session has been loaded
    if (sessionHasBeenLoaded) {
      // If session has subdomain, update Redux if it's different
      // This ensures subdomain from page configurations is always applied
      if (sessionSubdomain && sessionSubdomain !== currentSubdomain) {
        dispatch(setSubdomain(sessionSubdomain));
      }
      // Don't clear subdomain when session doesn't have it
      // Subdomain should persist across page switches until backend explicitly confirms there's no subdomain
      // The backend will handle clearing subdomain when user switches stores (by resetting session)
    }
  }, [session, sessionSubdomain, currentSubdomain, dispatch]);

  // Sync subdomain from Redux store with URL
  // On initial mount: Update URL immediately if subdomain is in Redux but not in URL
  // This ensures components render with subdomain from the start
  // On subsequent updates: Use setTimeout to prevent remounts
  useEffect(() => {
    // If Redux store has subdomain but URL doesn't
    if (currentSubdomain && currentSubdomain !== urlSubdomain) {
      // Get relative path (without subdomain)
      const relativePath = getRelativePath(location.pathname, urlSubdomain);
      
      // Build new path with subdomain using helper
      const newPath = getSubdomainPath(relativePath);
      
      // Only update if the path actually needs to change
      if (newPath !== location.pathname) {
        // Preserve query params
        const newUrl = location.search ? `${newPath}${location.search}` : newPath;
        
        // On initial mount: Update URL immediately so components render with subdomain from start
        // This causes an initial remount, but ensures correct subdomain is in URL from the beginning
        if (isInitialMount.current && !hasUpdatedUrl.current) {
          hasUpdatedUrl.current = true;
          history.replace(newUrl);
        } else if (!isInitialMount.current) {
          // On subsequent updates: Defer to prevent remounts during component lifecycle
          setTimeout(() => {
            history.replace(newUrl);
          }, 0);
        }
      }
    }
    // If Redux store doesn't have subdomain but URL does
    // Only remove if session_data has been loaded and confirmed there's no subdomain
    const sessionHasBeenLoaded = session && (
      session.area_name || 
      (session.area_id && session.area_id !== '') ||
      (session.outlet_id && session.outlet_id !== '')
    );
    
    if (!currentSubdomain && urlSubdomain && sessionHasBeenLoaded && !sessionSubdomain) {
      // Remove subdomain from URL only if session_data has been loaded and confirms there's no subdomain
      const relativePath = getRelativePath(location.pathname, urlSubdomain);
      const newUrl = location.search ? `${relativePath}${location.search}` : relativePath;
      
      // On initial mount: Update immediately
      if (isInitialMount.current && !hasUpdatedUrl.current) {
        hasUpdatedUrl.current = true;
        history.replace(newUrl);
      } else if (!isInitialMount.current) {
        // On subsequent updates: Defer to prevent remounts
        setTimeout(() => {
          history.replace(newUrl);
        }, 0);
      }
    }
    
    // Mark that initial mount is complete after first effect run
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }
  }, [currentSubdomain, urlSubdomain, session, sessionSubdomain, location.pathname, location.search, history]);

  return null; // This component doesn't render anything
};

export default SubdomainSync;

